'use client'

import { useState } from 'react'

const FAQ_LEFT = [
  // L'ancienne réponse citait « transcription, codification ou note technique »
  // comme un choix libre, alors que la note technique a toujours été réservée au
  // Pro — et que le rapport d'analyse l'est devenu le 2026-10-12. La liste qui
  // fait autorité est PRO_ONLY_MODULES (credit-costs.ts).
  { q: 'Puis-je utiliser un seul module ?',
    a: "Oui, les modules sont indépendants. L'offre gratuite donne accès à la transcription OU à la codification — un seul des deux, à votre choix. Le rapport d'analyse, la note technique et la correction du verbatim sont réservés au plan Pro." },
  // Ces deux réponses affirmaient « rien n'est archivé, rien n'est partagé » et
  // « toutes vos données sont effacées définitivement ». Les deux étaient
  // FAUSSES, et contredisaient la propre page /privacy du site :
  //   · les RÉSULTATS sont conservés par conception — c'est l'historique du
  //     tableau de bord, donc une fonctionnalité, pas un oubli ;
  //   · les verbatims sont transmis à OpenAI et Anthropic pour traitement,
  //     sous-traitants que /privacy déclare explicitement.
  // Ce qui est vrai, et vérifié : l'audio déposé est supprimé dès la
  // transcription terminée (`deleteStorageFile`), et les dépôts abandonnés sont
  // purgés sous 24 h (`purgeOrphanFiles`). Aucune route DELETE n'existe côté
  // utilisateur : la suppression des résultats passe par une demande, ce que
  // /privacy prévoit déjà au titre du droit à l'effacement.
  { q: 'Mes données sont-elles stockées sur vos serveurs ?',
    a: "Vos transcriptions et documents sont enregistrés dans votre historique personnel, pour que vous puissiez les retrouver et les télécharger à tout moment. Ils sont privés et accessibles uniquement depuis votre compte. Les fichiers audio sont supprimés une fois la transcription terminée." },
  // « tant que votre compte est actif » et non « jusqu'à ce que vous les
  // supprimiez » : AUCUNE route DELETE n'existe dans l'API, aucun appel client,
  // aucun bouton de suppression dans l'historique. La politique RLS
  // « Users can delete own transcriptions » l'autoriserait en base, mais le
  // produit ne l'expose pas — c'est l'expérience réelle qui compte ici.
  { q: 'Les données sont-elles supprimées après traitement ?',
    a: "L'audio, oui : il est supprimé dès la fin de la transcription. Vos transcriptions et documents restent dans votre historique tant que votre compte est actif." },
  // Reformulée sur l'inventaire réel des boutons de téléchargement : aucun
  // export PDF n'existe, et la codification sort en Excel, pas en Word.
  // L'ancienne réponse promettait « .docx et PDF » pour les quatre modules.
  { q: 'Puis-je exporter mes résultats vers Word ou Excel ?',
    a: "Oui. Notes techniques, transcriptions, verbatims corrigés et rapports s'exportent en Word (.docx) ; les transcriptions et verbatims aussi en texte brut (.txt) ; les tableaux de codification en Excel (.xlsx). L'export PDF n'est pas proposé." },
  { q: 'Qualyse fonctionne-t-il pour les entretiens en français ?',
    a: "Oui. Optimisé pour le français académique et local — Côte d'Ivoire, Sénégal, Cameroun, France, Belgique et toute la francophonie." },
]

const FAQ_RIGHT = [
  // « Taille max : 25 Mo » était faux d'un facteur 80 : MAX_FILE_SIZE_MB = 2048,
  // soit 2 Go — valeur déjà annoncée par les CGU, que la FAQ contredisait donc.
  // La liste des formats était aussi incomplète : ACCEPTED_EXTENSIONS en compte
  // DOUZE, les cinq derniers étant décodés par ffmpeg avant envoi.
  { q: 'Quels formats audio sont acceptés ?',
    a: "MP3, MP4, WAV, M4A, MOV, AVI, WebM, AAC, OPUS, M4R, FLAC et AMR. Taille maximale : 2 Go par fichier. Pour les enregistrements longs, Qualyse découpe et reconstitue automatiquement." },
  { q: 'Combien de temps prend une transcription ?',
    a: "En moyenne, une heure d'entretien est transcrite en moins de 5 minutes. Correction et codification s'effectuent en quelques minutes supplémentaires." },
  { q: 'Qualyse convient-il aux étudiants en master et doctorat ?',
    a: "Oui, c'est l'un des profils pour lequel Qualyse a été conçu — de la note technique jusqu'au rapport final, avec les standards académiques." },
  { q: "Faut-il installer un logiciel ?",
    a: "Non. Qualyse est 100% en ligne. Aucune installation. Accessible depuis n'importe quel navigateur, sur ordinateur, tablette ou mobile." },
  { q: "Comment contacter le support ?",
    // « prioritaire pour les abonnés Pro, Recherche et Cabinet » retiré : les
    // plans « Recherche » et « Cabinet » N'EXISTENT PAS (seuls Gratuit et Pro
    // sont définis), et aucun traitement prioritaire n'est implémenté.
    a: "Depuis la page Contact de la plateforme, ou via la bulle de support en bas de l'écran." },
]

export default function FaqSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const renderCol = (items: {q:string;a:string}[], offset: number) => (
    <div style={{ background:'#fff', borderRadius:16, overflow:'hidden', border:'1px solid #e8e4dc' }}>
      {items.map((item, i) => {
        const idx = i + offset
        const isOpen = openFaq === idx
        return (
          <div key={idx} className={`faq-item${isOpen ? ' faq-open' : ''}`}>
            <button className="faq-trigger" onClick={() => setOpenFaq(isOpen ? null : idx)}>
              <span className="faq-num">{String(idx + 1).padStart(2, '0')}</span>
              <span className="faq-q">{item.q}</span>
              <span className="faq-icon">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1v10M1 6h10" stroke="#555" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </span>
            </button>
            <div className={`faq-body${isOpen ? ' faq-body-open' : ''}`}>
              <div className="faq-inner">
                <p className="faq-answer">{item.a}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <section style={{ background: '#f7f5f0', borderBottom: '1px solid #e8e4dc' }}>
      <style>{`
        .faq-item { border-bottom:1px solid #ede9e2; transition:background 0.15s; }
        .faq-item:last-child { border-bottom:none; }
        .faq-item:hover { background:#ece8e0; }
        .faq-item.faq-open { background:#eef5ea; border-bottom-color:#c5ddb8; }
        .faq-trigger { width:100%; display:flex; align-items:center; gap:14px; padding:18px 20px; cursor:pointer; background:none; border:none; text-align:left; }
        .faq-num { font-size:11px; font-weight:800; color:#2d5a27; font-family:monospace; letter-spacing:0.06em; flex-shrink:0; width:24px; }
        .faq-q { flex:1; font-size:17px; font-weight:600; color:#1a1a1a; line-height:1.4; }
        .faq-item.faq-open .faq-q { color:#2d5a27; }
        .faq-icon { width:24px; height:24px; border-radius:50%; border:1.5px solid #d4cfc6; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:transform 0.22s ease, border-color 0.15s, background 0.15s; }
        .faq-item.faq-open .faq-icon { transform:rotate(45deg); border-color:#2d5a27; background:#e8f0e2; }
        .faq-body { display:grid; grid-template-rows:0fr; transition:grid-template-rows 0.28s cubic-bezier(.4,0,.2,1); }
        .faq-body.faq-body-open { grid-template-rows:1fr; }
        .faq-inner { overflow:hidden; }
        .faq-answer { padding:0 20px 18px 58px; font-size:15px; color:#6b6560; line-height:1.72; }
      `}</style>
      <div className="faq-outer" style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 40px' }}>
        <div style={{ textAlign:'center', marginBottom: 40 }}>
          <span style={{ display:'inline-block', padding:'5px 14px', background:'#e8f0e2', color:'#2d5a27', borderRadius:100, fontSize:11, fontWeight:700, fontFamily:'monospace', letterSpacing:'0.07em', textTransform:'uppercase', marginBottom:14 }}>
            FAQ
          </span>
          <h2 style={{ fontFamily:'Georgia, serif', fontSize:'clamp(22px, 2.2vw, 32px)', fontWeight:800, lineHeight:1.15, letterSpacing:'-0.5px', color:'#1a1a1a', margin:0 }}>
            Questions <em style={{ fontStyle:'italic', color:'#2d5a27' }}>fréquentes</em>
          </h2>
        </div>
        <div className="faq-grid-cols" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          {renderCol(FAQ_LEFT, 0)}
          {renderCol(FAQ_RIGHT, 5)}
        </div>
      </div>
    </section>
  )
}
